import { Suspense, lazy, useEffect, useState } from 'react';
import { Layout, Breadcrumb } from 'antd';
import { Outlet, useLocation, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import AppHeader from './AppHeader';
import PrimaryRail from './PrimaryRail';
import ModuleSidebar from './ModuleSidebar';
import CommandPalette from './CommandPalette';
import RecentsDrawer from './RecentsDrawer';
import ReadOnlyBanner from './ReadOnlyBanner';
import { readRouteRecents, recordRouteVisit, type RouteRecent } from './recents';
import { useDemoMode } from '@/pages/DemoMode';
import { useReadOnly } from '@/hooks/useReadOnly';

// DemoPlayer is lazy-loaded so the chunk only ships when demo mode
// activates — keeps the initial bundle clean for non-demo users.
const DemoPlayer = lazy(() => import('@/pages/Demo'));

const { Content } = Layout;

/**
 * App shell layout (Quantis-pattern).
 *
 *   ┌──────────────────────────────────────────────┐
 *   │ AppHeader                                    │
 *   ├────┬───────────┬─────────────────────────────┤
 *   │    │           │                             │
 *   │ P  │ Module    │   Content (Outlet)          │
 *   │ R  │ Sidebar   │                             │
 *   │    │ (240 px,  │                             │
 *   │    │ collapses │                             │
 *   │    │ to 0 on   │                             │
 *   │    │ no-match) │                             │
 *   │ 64 │           │                             │
 *   └────┴───────────┴─────────────────────────────┘
 *
 * - PrimaryRail (PR) is always visible — icon-only module switcher.
 * - ModuleSidebar shows the active module's deep nav. It collapses to
 *   width 0 when the current route has no module config (Landing, About,
 *   Dashboard) so the content gets full width.
 * - The hamburger in the header still toggles the module sidebar so
 *   keyboard users on small screens can recover the content area.
 */
export default function AppShell() {
  const { t, i18n } = useTranslation();
  const [moduleSidebarCollapsed, setModuleSidebarCollapsed] = useState(false);
  const [cmdkOpen, setCmdkOpen] = useState(false);
  const [recentsOpen, setRecentsOpen] = useState(false);
  const [recents, setRecents] = useState<RouteRecent[]>(() => readRouteRecents());
  const location = useLocation();
  const demoActive = useDemoMode((s) => s.active);
  const readOnly = useReadOnly();

  // Record every navigation in the recents store. The drawer reads via
  // setRecents so we don't need to re-read localStorage on every render.
  useEffect(() => {
    setRecents(recordRouteVisit(location.pathname));
  }, [location.pathname]);

  // Global Cmd/Ctrl+K shortcut. Skips when the user is typing in an
  // input/textarea so it doesn't fight in-page form editing. Modal close
  // on Esc is owned by AntD Modal — we only handle the open trigger here.
  useEffect(() => {
    const handle = (ev: KeyboardEvent): void => {
      const target = ev.target as HTMLElement | null;
      const isEditable =
        target instanceof HTMLInputElement ||
        target instanceof HTMLTextAreaElement ||
        target?.isContentEditable === true;
      if ((ev.metaKey || ev.ctrlKey) && (ev.key === 'k' || ev.key === 'K')) {
        if (isEditable && !cmdkOpen) return;
        ev.preventDefault();
        setCmdkOpen((v) => !v);
      }
    };
    window.addEventListener('keydown', handle);
    return () => window.removeEventListener('keydown', handle);
  }, [cmdkOpen]);

  const breadcrumbItems = buildBreadcrumb(location.pathname, t, i18n.exists);

  return (
    <Layout className="ga-app-layout">
      <AppHeader
        onToggleSider={() => setModuleSidebarCollapsed((c) => !c)}
        onOpenRecents={() => setRecentsOpen(true)}
      />
      {readOnly && <ReadOnlyBanner />}
      <Layout>
        <PrimaryRail />
        <ModuleSidebar collapsed={moduleSidebarCollapsed} />
        <Layout>
          <Content className="ga-content">
            <Breadcrumb
              items={breadcrumbItems}
              style={{ marginBottom: 16 }}
              className="ga-no-print"
            />
            {/* `key` re-mounts the route on every navigation so the fade
             * keyframe in global.css triggers per page change. */}
            <div className="ga-route-content" key={location.pathname}>
              <Outlet />
            </div>
          </Content>
        </Layout>
      </Layout>
      {/* Demo overlay — mounts only when demo mode is active. The DemoPlayer
       * navigates the router on a timer; lifting it here means it persists
       * across every route change for the duration of the demo. */}
      {demoActive && (
        <Suspense fallback={null}>
          <DemoPlayer />
        </Suspense>
      )}
      {/* Command palette — global ⌘K / Ctrl+K target. Indexed lazily on
       * open from the live mock store snapshot. */}
      <CommandPalette open={cmdkOpen} onClose={() => setCmdkOpen(false)} />
      <RecentsDrawer
        open={recentsOpen}
        onClose={() => setRecentsOpen(false)}
        recents={recents}
        onCleared={() => setRecents([])}
      />
    </Layout>
  );
}

function buildBreadcrumb(
  pathname: string,
  t: (k: string) => string,
  exists: (k: string) => boolean,
) {
  const parts = pathname.split('/').filter(Boolean);
  const items: Array<{ title: React.ReactNode }> = [
    { title: <Link to="/">{t('breadcrumb.home')}</Link> },
  ];
  let acc = '';
  for (const p of parts) {
    acc += `/${p}`;
    items.push({ title: <Link to={acc}>{labelForSegment(p, t, exists)}</Link> });
  }
  return items;
}

function labelForSegment(
  segment: string,
  t: (k: string) => string,
  exists: (k: string) => boolean,
): string {
  if (exists(`common:nav.${segment}`)) return t(`nav.${segment}`);
  if (exists(`common:breadcrumb.segments.${segment}`)) {
    return t(`breadcrumb.segments.${segment}`);
  }
  return humanize(segment);
}

const ACRONYMS = new Set([
  'ar', 'ap', 'gl', 'po', 'vat', 'coa', 'rfq', 'rfp', 'kpi',
  'bim', 'boq', 'wbs', 'om', 'ipc', 'vo', 'hr', 'mr', 'bd',
]);

function humanize(segment: string): string {
  return segment
    .split('-')
    .map((word) =>
      ACRONYMS.has(word.toLowerCase())
        ? word.toUpperCase()
        : word.charAt(0).toUpperCase() + word.slice(1),
    )
    .join(' ');
}
